import { Ref, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  User,
  UserJwtPayload,
  availlableFeaturesSlug,
} from '@/store/user/userStore.d'
import { login as authLogin } from '@/api/auth/auth'
import { jwtDecode } from 'jwt-decode'
import dayjs from 'dayjs'
import { ApiResponse } from '@/api/api'
import { useStorage } from '@vueuse/core'
import { useRouter, useRoute } from 'vue-router'
import createHttpClient from '@/api/httpClient'
import * as Sentry from '@sentry/vue'
import { ProfileInformationData } from '@/api/profile/profile.d'
import { getProfiles } from '@/api/profile/profile'
import { AddressInformationData } from '@/api/account/address'
import { getAddresses } from '@/api/account/account'
import { useGeolocationStore } from '../geolocation/geolocationStore'
import MessageService from '@/components/feedback/message/messageService'
import i18n from '@/plugins/i18n'
import { useDialogTimeSlotStore } from '../timeslot/dialogTimeSlotStore'
import { useAskPatientPresenceToPractitionerStore } from '../askPatientPresenceToPractitioner/askPatientPresenceToPractitionerStore'
import { useDialogAvisConsultationStore } from '../dialogAvisConsultationStore/dialogAvisConsultationStore'
import { formatAddress } from '@/utils/formatAddress'
import { useDialogYourAbsenceStore } from '../dialogYourAbsenceStore/dialogYourAbsenceStore'
import { useAppStore } from '@/store/app'
import { useSubscriptionStore } from '@/store/subscription/subscriptionStore'

const { t } = i18n.global
const httpClient = createHttpClient()

export const useUserStore = defineStore('user', () => {
  const user = useStorage('user', {} as User | undefined | null)
  const isLogged = ref(false)
  const router = useRouter()
  const route = useRoute()

  const isPageVisible = ref<boolean>(document.visibilityState === 'visible')

  const appStore = useAppStore()
  // const subscriptionStore = useSubscriptionStore()

  const init = () => {
    const token = user.value?.token
    if (token) {
      const decoded = jwtDecode<UserJwtPayload>(token)
      const expirationDate = dayjs.unix(decoded.exp)
      if (dayjs().isBefore(expirationDate)) {
        isLogged.value = true
        Sentry.setUser({ id: user.value.id, email: user.value.email })
      } else {
        refreshToken()
      }
    }
  }

  const login = async () => {
    const response = await authLogin()
    handleLoginResponse(response)
  }

  const refreshToken = async () => {
    if (!user.value?.refreshToken) {
      return Promise.reject('No refresh token')
    }
    try {
      const response = await httpClient.post(
        '/refresh-token',
        { refreshToken: user.value.refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )
      handleLoginResponse({
        status: response.status,
        data: response.data,
      } as ApiResponse<any>)
    } catch (error) {
      handleLoginResponse({
        status: error.status,
        data: error.data,
      } as ApiResponse<any>)
    }
  }

  const handleLoginResponse = async (response: ApiResponse<any>) => {
    if (response.status === 200) {
      user.value = response.data
      isLogged.value = true
      Sentry.setUser({ id: user.value.id, email: user.value.email })

      if (user.value.profilType === 'patient') {
        appStore.navRole = 'patient'
      }

      // Update geolocation in localstorage
      const geolocationStore = useGeolocationStore()
      geolocationStore.updateStoredLocation({
        city: response.data.appointmentCity,
        coords: {
          latitude: response.data.appointmentLatitude,
          longitude: response.data.appointmentLongitude,
        },
      })

      // lancer un call API pour chaque modale de consultation à la connexion
      if (user.value.profilType === 'practitioner') {
        // Store to ask if patient were present to practitioner
        const dialogAskPractitionerConsultationStore =
          useAskPatientPresenceToPractitionerStore()
        await dialogAskPractitionerConsultationStore.fetchConsultations()
      }

      if (
        user.value.profilType === 'practitioner' &&
        !user.value.isSignUpIncomplete
      ) {
        loadCalWidget()
      }

      if (
        user.value.profilType === 'patient' ||
        user.value.profilType === 'practitioner'
      ) {
        // Store to ask reviews to patient
        const dialogAvisConsultationStore = useDialogAvisConsultationStore()
        await dialogAvisConsultationStore.fetchConsultations()

        // Ask to confirm the absence to the patient
        const dialogYourAbsenceStore = useDialogYourAbsenceStore()
        await dialogYourAbsenceStore.fetchConsultations()
      }

      // if (
      //   user.value.profilType === 'practitioner' &&
      //   user.value.isSignUpIncomplete
      // ) {
      //   return await router.push({ name: 'Subscription' })
      // }
      return true
    } else {
      logout()
    }
  }

  const logout = async (redirect: boolean = true) => {
    const redirectPath = route.fullPath
    isLogged.value = false
    await unloadCalWidget()

    if (redirect) {
      await router.push({ name: 'Login', query: { redirectUrl: redirectPath } })
    }
    user.value = null
    localStorage.removeItem('user')
    localStorage.removeItem('addresses')
    localStorage.removeItem('profiles')
    localStorage.removeItem('vacations')
    localStorage.removeItem('appointmentReasons')
    localStorage.removeItem('dialogTimeSlotCreationProfileAddress')
    const subscriptionStore = useSubscriptionStore()
    subscriptionStore.resetSubscriptionStatusAfterLogout()
    subscriptionStore.discountCodes = []
  }

  const profiles: Ref<ProfileInformationData[]> = ref([])
  const initProfiles = async () => {
    // try {
    //   const res: ApiResponse<ProfileInformationData[]> = await getProfiles()
    //   if (res.status !== 200) throw Error(JSON.stringify(res))
    //   profiles.value = res.data
    //   // Set a default profile in storage if the one already in storage doesn't exist anymore
    //   const dialogTimeSlotStore = useDialogTimeSlotStore()
    //   if (
    //     !dialogTimeSlotStore.dialogTimeSlotCreationProfileAddress.profile ||
    //     !profiles.value.some(
    //       profile =>
    //         profile.id ===
    //         dialogTimeSlotStore.dialogTimeSlotCreationProfileAddress.profile.id,
    //     )
    //   ) {
    //     dialogTimeSlotStore.dialogTimeSlotCreationProfileAddress.profile =
    //       profiles.value[0]
    //   }
    // } catch (error) {
    //   console.error(error)
    //   MessageService.error(t('profile.errorGetProfile'))
    // }
  }

  const addresses: Ref<AddressInformationData[]> = ref([])
  const initAddresses = async () => {
    // try {
    //   const res: ApiResponse<AddressInformationData[]> = await getAddresses()
    //   if (res.status != 200) throw Error(JSON.stringify(res))
    //   addresses.value = res.data.map(address => ({
    //     ...address,
    //     formattedAddress: formatAddress(address, true),
    //   }))
    //   // Set a default address in storage if the one already in storage doesn't exist anymore
    //   const dialogTimeSlotStore = useDialogTimeSlotStore()
    //   if (
    //     !dialogTimeSlotStore.dialogTimeSlotCreationProfileAddress.address ||
    //     !addresses.value.some(
    //       addr =>
    //         addr.id ===
    //         dialogTimeSlotStore.dialogTimeSlotCreationProfileAddress.address.id,
    //     )
    //   ) {
    //     dialogTimeSlotStore.dialogTimeSlotCreationProfileAddress.address =
    //       addresses.value[0]
    //   }
    // } catch (error) {
    //   console.error(error)
    // }
  }

  const unAuthorizedFeature = (
    featureSlug: availlableFeaturesSlug,
  ): boolean => {
    return user.value.features?.includes(featureSlug) ?? false
  }

  const loadCalWidgetOld = () => {
    return false
    // Déjà chargé ? Ne rien faire
    // const script = document.getElementById('cal-script')

    // if (script) return;

    // Définir Cal AVANT de charger le script
    ;(function (C: any, A: any, L: any) {
      let p = function (a: any, ar: any) {
        a.q.push(ar)
      }
      let d = C.document
      C.Cal =
        C.Cal ||
        function () {
          let cal: any = C.Cal
          let ar = arguments
          if (!cal.loaded) {
            cal.ns = {}
            cal.q = cal.q || []
            const s = d.createElement('script')
            s.src = A
            s.id = 'cal-script'
            d.head.appendChild(s)
            cal.loaded = true
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments)
            }
            const namespace = ar[1]
            api.q = api.q || []
            if (typeof namespace === 'string') {
              cal.ns[namespace] = cal.ns[namespace] || api
              p(cal.ns[namespace], ar)
              p(cal, ['initNamespace', namespace])
            } else {
              p(cal, ar)
            }
            return
          }
          p(cal, ar)
        }
    })(window, 'https://cal.holitime.org/embed/embed.js', 'init')

    // Appel après avoir injecté
    ;(window as any).Cal('init', 'votre-lancement-holitime', {
      origin: 'https://cal.holitime.org',
    })
    ;(window as any).Cal.ns['votre-lancement-holitime']('floatingButton', {
      calLink: 'team/praticiens/votre-lancement-holitime',
      config: { layout: 'month_view', theme: 'light' },
      buttonText: 'Être rappelé',
      buttonColor: '#153d35',
    })
    ;(window as any).Cal.ns['votre-lancement-holitime']('ui', {
      theme: 'light',
      cssVarsPerTheme: {
        light: { 'cal-brand': '#153d35' },
        dark: { 'cal-brand': '#12c29b' },
      },
      hideEventTypeDetails: false,
      layout: 'month_view',
    })
  }

  const loadCalWidget = () => {
    ;(function (C: any, A: any, L: any) {
      console.log('load widget')
      let p = function (a, ar) {
        a.q.push(ar)
      }
      let d = C.document
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal
          let ar = arguments

          if (!cal.loaded) {
            cal.ns = {}
            cal.q = cal.q || []
            d.head.appendChild(d.createElement('script')).src = A
            cal.loaded = true
          }

          if (ar[0] === L) {
            const api = function () {
              p(api, arguments)
            }
            const namespace = ar[1]
            api.q = api.q || []
            if (typeof namespace === 'string') {
              cal.ns[namespace] = cal.ns[namespace] || api
              p(cal.ns[namespace], ar)
              p(cal, ['initNamespace', namespace])
            } else p(cal, ar)
            return
          }
          p(cal, ar)
        }
    })(window, 'https://cal.holitime.org/embed/embed.js', 'init')
    ;(window as any).Cal('init', 'votre-lancement-holitime', {
      origin: 'https://cal.holitime.org',
    })

    // Important: Please add the following attributes to the element that should trigger the calendar to open upon clicking.
    // `data-cal-link="team/praticiens/votre-lancement-holitime"`
    // data-cal-namespace="votre-lancement-holitime"
    // `data-cal-config='{"layout":"month_view"}'`
    ;(window as any).Cal.ns['votre-lancement-holitime']('ui', {
      cssVarsPerTheme: {
        light: {
          'cal-brand': '#153d35',
        },
        dark: { 'cal-brand': '#12c29b' },
      },
      hideEventTypeDetails: false,
      layout: 'month_view',
    })
  }

  const unloadCalWidget = () => {
    return false
    document.querySelectorAll('cal-floating-button').forEach(el => {
      ;(el as HTMLElement).style.display = 'none'
    })
  }

  return {
    user,
    isLogged,
    init,
    login,
    refreshToken,
    handleLoginResponse,
    logout,
    profiles,
    initProfiles,
    addresses,
    initAddresses,
    unAuthorizedFeature,
    unloadCalWidget,
    loadCalWidget,
    isPageVisible,
  }
})
